/**
 * CoachPlaybook - Main Application Component (Mobile-First PWA)
 * Version: 0.6.0
 * Description: Broadcast-quality dark theme multi-sport coaching platform
 */

import React, { useState, useEffect } from 'react';
import Dashboard from './components/settings/Dashboard';
import DataManagement from './components/settings/DataManagement';
import Roster from './components/roster/Roster';
import PlayDesigner from './components/playbook/PlayDesigner';
import PlaybookStudio from './components/studio/PlaybookStudio';
import TeamSettings from './components/settings/TeamSettings';
import Scouting from './components/stats/Scouting';
import Stats from './components/stats/Stats';
import JerseyIcon from './components/common/JerseyIcon';
import LearnPage from './components/learn/LearnPage';
import IconsPage from './components/icons/IconsPage';
import { getSetting, setSetting } from './utils/db';
import { useAuth } from './utils/auth/AuthContext';
import { useSport } from './components/common/SportContext';
import { useTheme } from './utils/theme';
import AllowlistPanel from './components/admin/AllowlistPanel';

// Lazy load auth components
let LoginPage = null;
let AuthCallback = null;
try {
  LoginPage = require('./components/auth/LoginPage').default;
  AuthCallback = require('./components/auth/AuthCallback').default;
} catch (e) { /* Auth components not yet built */ }

const APP_VERSION = '0.6.0';

// --- SVG Icon path data (20x20 viewBox) ---
const NAV_ICONS = {
  dashboard: 'M3 3h7v7H3V3zm11 0h7v7h-7V3zM3 14h7v7H3v-7zm11 0h7v7h-7v-7z',
  plays: 'M9 2H4a2 2 0 00-2 2v16a2 2 0 002 2h16a2 2 0 002-2V9l-7-7zm4 18H4V4h5v5h4v11zm2-14V3l5 5h-3a2 2 0 01-2-2z',
  roster: 'M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
  learn: 'M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z',
  stats: 'M3 13h2v8H3v-8zm4-4h2v12H7V9zm4-4h2v16h-2V5zm4 6h2v10h-2V11zm4-3h2v13h-2V8z',
  scouting: 'M15.5 14h-.79l-.28-.27A6.47 6.47 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z',
  teamSettings: 'M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.49.49 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.48.48 0 00-.48-.41h-3.84a.48.48 0 00-.48.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 00-.59.22L2.74 8.87a.48.48 0 00.12.61l2.03 1.58c-.05.3-.07.63-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.26.41.48.41h3.84c.24 0 .44-.17.48-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1115.6 12 3.61 3.61 0 0112 15.6z',
  data: 'M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z',
};

// Sport-specific top icons (24x24 viewBox)
const SPORT_ICONS = {
  basketball: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L10 14v1c0 1.1.9 2 2 2v1.93zm6.9-2.54A3.98 3.98 0 0016 15c-1.1 0-2-.9-2-2v-1l-4-4-1.79-1.79C9.4 5.5 10.65 5 12 5c4.08 0 7.44 3.05 7.93 7h-2.95l1.92 3.39z',
  football: 'M20.31 3.69a2.1 2.1 0 00-2.63-.28C14.52 5.66 9.62 8.2 6.41 11.41c-3.21 3.21-5.75 8.11-8 11.27a2.1 2.1 0 00.28 2.63 2.1 2.1 0 002.63.28c3.16-2.25 8.06-4.79 11.27-8 3.21-3.21 5.75-8.11 8-11.27a2.1 2.1 0 00-.28-2.63zM8 16l-2-2 5-5 2 2-5 5z',
  soccer: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 2.07c2.52.41 4.67 1.89 5.97 3.93H14l-1-3.93zm-2 0L10 8H5.03c1.3-2.04 3.45-3.52 5.97-3.93zM4.2 14L4 12c0-1.04.2-2.04.57-2.95L7.2 14H4.2zM11 19.93c-2.52-.41-4.67-1.89-5.97-3.93H10l1 3.93zm1-5.93H8.8l-1.6-4h9.6l-1.6 4H12zm1 5.93l1-3.93h4.97c-1.3 2.04-3.45 3.52-5.97 3.93zM16.8 14l2.63-4.95c.37.91.57 1.91.57 2.95l-.2 2h-3z',
  baseball: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM5.61 16.78C4.6 15.45 4 13.8 4 12s.6-3.45 1.61-4.78c.08.58.28 1.13.58 1.62l.6 1c.24.4.56.76.92 1.04-.36.28-.68.64-.92 1.04l-.6 1c-.3.49-.5 1.04-.58 1.62v.24zm12.78 0v-.24c-.08-.58-.28-1.13-.58-1.62l-.6-1a3.49 3.49 0 00-.92-1.04c.36-.28.68-.64.92-1.04l.6-1c.3-.49.5-1.04.58-1.62C19.4 8.55 20 10.2 20 12s-.6 3.45-1.61 4.78z',
  hockey: 'M22 17v-2h-5.17L14 12.17V7a2 2 0 00-4 0v5.17L7.17 15H2v2h6l4-4 4 4h6z',
  volleyball: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8 0-1.85.63-3.55 1.69-4.9L12 12l6.31-4.9A7.95 7.95 0 0120 12c0 4.41-3.59 8-8 8z',
};

const App = () => {
  // Initial tab honors ?tab=<id> in the URL so hidden pages (e.g. icons on
  // mobile where the bottom bar is full) can be reached by direct link.
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') return 'dashboard';
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    const valid = new Set([
      'dashboard', 'plays', 'roster', 'stats', 'scouting', 'learn',
      'data', 'teamSettings', 'admin', 'icons',
    ]);
    return valid.has(t) ? t : 'dashboard';
  });
  const [teamName, setTeamName] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth <= 1024);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth >= 768 && window.innerWidth <= 1024);
  const [jerseyTemplates, setJerseyTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState('default');
  const [refreshKey, setRefreshKey] = useState(0); // Force refresh Roster

  // Get the active jersey for nav icon - prefer isDefault, then activeTemplateId, then first
  const activeJersey = jerseyTemplates.find(t => t.isDefault) || 
                       jerseyTemplates.find(t => t.id === activeTemplateId) || 
                       jerseyTemplates[0] || 
                       { fillColor: '#2563eb', outlineColor: '#1e40af', numberColor: '#ffffff', numberOutline: '#1e40af' };

  // Migrate jersey template to include all required fields
  const migrateTemplate = (template) => ({
    // Start with all defaults
    id: 'default',
    name: 'Home',
    fillColor: '#2563eb',
    outlineColor: '#1e40af',
    numberColor: '#ffffff',
    numberOutline: '#1e40af',
    numberOutline2: null,
    textColor: '#ffffff',
    textOutline: '#1e40af',
    textOutline2: null,
    fontFamily: 'Impact, Haettenschweiler, sans-serif',
    logoUrl: null,
    numberY: 55,
    nameY: 5,
    numberSize: 100,
    nameSize: 100,
    jerseyBorderWeight: 12,
    numberBorderWeight: 6,
    numberBorderWeight2: 0,
    nameBorderWeight: 4,
    nameBorderWeight2: 0,
    // Override with saved values
    ...template
  });

  // Load team name and jerseys from IndexedDB
  useEffect(() => {
    const loadSettings = async () => {
      const savedTeamName = await getSetting('teamName', '');
      if (savedTeamName) setTeamName(savedTeamName);
      
      // Load all jersey templates and MIGRATE them
      const templates = await getSetting('jerseyTemplates', []);
      const activeId = await getSetting('activeJerseyTemplate', 'default');
      
      if (templates && templates.length > 0) {
        // Migrate all templates to ensure they have all required fields
        const migratedTemplates = templates.map(migrateTemplate);
        setJerseyTemplates(migratedTemplates);
        
        // Save migrated templates back to IndexedDB
        await setSetting('jerseyTemplates', migratedTemplates);
      }
      setActiveTemplateId(activeId);
    };
    loadSettings();
  }, []);

  // Reload everything when team settings closes
  // Refresh team data (jerseys, team name) - called when leaving Team Settings
  const refreshTeamData = async () => {
    // Refresh all templates (with migration)
    const templates = await getSetting('jerseyTemplates', []);
    const activeId = await getSetting('activeJerseyTemplate', 'default');
    
    if (templates && templates.length > 0) {
      const migratedTemplates = templates.map(migrateTemplate);
      setJerseyTemplates(migratedTemplates);
    }
    setActiveTemplateId(activeId);
    
    // Refresh team name
    const savedTeamName = await getSetting('teamName', '');
    if (savedTeamName) setTeamName(savedTeamName);
    
    // Force Roster to re-render with new templates
    setRefreshKey(prev => prev + 1);
  };

  // Save team name to IndexedDB
  useEffect(() => {
    if (teamName) {
      setSetting('teamName', teamName);
    }
  }, [teamName]);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width <= 1024);
      // Auto-close menu on desktop
      if (width >= 768) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Auto-collapse sidebar on tablet when entering PlayDesigner
  useEffect(() => {
    if (isTablet && activeTab === 'plays') {
      setSidebarCollapsed(true);
    }
  }, [activeTab, isTablet]);

  // Theme context
  const { mode: themeMode, theme: themeColors, toggleTheme } = useTheme();

  // Sport context for sidebar sport icon
  const sportCtx = useSport();
  const currentSportId = sportCtx?.currentSport || 'basketball';

  // Auth context (must be called before any early returns to respect hook rules)
  const auth = useAuth();

  const isAdmin = auth?.user?.is_admin;

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', iconKey: 'dashboard' },
    { id: 'plays', label: 'Playbook Studio', iconKey: 'plays' },
    { id: 'roster', label: 'Team Roster', iconKey: 'roster' },
    { id: 'learn', label: 'Learn', iconKey: 'learn' },
    { id: 'stats', label: 'Stats', iconKey: 'stats' },
    { id: 'scouting', label: 'Scouting', iconKey: 'scouting' },
    { id: 'divider', label: '', iconKey: '' },
    { id: 'teamSettings', label: 'Team Settings', iconKey: 'teamSettings' },
    { id: 'icons', label: 'Custom Icons', iconKey: 'teamSettings' },
    { id: 'data', label: 'Data Management', iconKey: 'data' },
    ...(isAdmin ? [{ id: 'admin', label: 'Admin', iconKey: 'teamSettings' }] : []),
  ];

  // Mobile bottom bar items (5 max)
  const mobileNavItems = [
    { id: 'dashboard', label: 'Home', iconKey: 'dashboard' },
    { id: 'plays', label: 'Plays', iconKey: 'plays' },
    { id: 'roster', label: 'Roster', iconKey: 'roster' },
    { id: 'stats', label: 'Stats', iconKey: 'stats' },
    { id: 'teamSettings', label: 'Settings', iconKey: 'teamSettings' },
  ];

  const handleNavClick = (tabId) => {
    // Refresh team data when leaving Team Settings tab
    if (activeTab === 'teamSettings' && tabId !== 'teamSettings') {
      refreshTeamData();
    }
    setActiveTab(tabId);
    setMenuOpen(false); // Close menu on mobile after selection
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard onNavigate={handleNavClick} teamName={teamName} />;
      case 'roster':
        return <Roster 
          key={refreshKey}
          teamName={teamName} 
          jerseyTemplates={jerseyTemplates}
          activeTemplateId={activeTemplateId}
        />;
      case 'plays':
        return <PlaybookStudio />;
      case 'stats':
        return <Stats onNavigate={handleNavClick} />;
      case 'scouting':
        return <Scouting onNavigate={handleNavClick} />;
      case 'learn':
        return <LearnPage onNavigate={handleNavClick} />;
      case 'data':
        return <DataManagement />;
      case 'icons':
        return <IconsPage />;
      case 'teamSettings':
        return <TeamSettings />;
      case 'admin':
        return isAdmin ? <AllowlistPanel /> : <Dashboard onNavigate={handleNavClick} teamName={teamName} />;
      default:
        return <Dashboard onNavigate={handleNavClick} teamName={teamName} />;
    }
  };

  // Auth gate
  // Handle OAuth callback
  if (window.location.pathname === '/auth/callback' || window.location.search.includes('code=')) {
    if (AuthCallback) return <AuthCallback />;
  }

  // Show login if not authenticated — BYPASSED FOR PREVIEW BUILD
  if (false && auth && !auth.isLoading && !auth.isLoggedIn && LoginPage) {
    return <LoginPage />;
  }

  // Show loading while auth initializes
  if (auth && auth.isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f172a' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏆</div>
          <div style={{ fontSize: '14px' }}>Loading CoachPlaybook...</div>
        </div>
      </div>
    );
  }

  // --- Broadcast Theme Constants (derived from ThemeContext) ---
  const THEME = {
    sidebarBg: themeColors.bgPanel,
    sidebarBorder: themeColors.border,
    activeItemBg: themeColors.bgCard,
    activeBorder: themeColors.accent,
    hoverBg: themeMode === 'dark' ? '#141a27' : '#f1f5f9',
    textActive: themeColors.text,
    textMuted: themeColors.textMuted,
    textDim: themeColors.borderLight,
    contentBg: themeColors.bg,
    mobilBarBg: themeColors.glass,
    accentDot: themeColors.accent,
    versionBg: themeMode === 'dark' ? '#141a27' : '#e2e8f0',
  };

  // Render a nav SVG icon
  const renderNavIcon = (iconKey, isActive) => {
    const path = NAV_ICONS[iconKey];
    if (!path) return null;
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill={isActive ? THEME.textActive : THEME.textMuted}
        style={{ flexShrink: 0, transition: 'fill 0.2s ease' }}
      >
        <path d={path} />
      </svg>
    );
  };

  // Render the sport icon for the logo area
  const renderSportIcon = () => {
    const path = SPORT_ICONS[currentSportId] || SPORT_ICONS.basketball;
    return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill={THEME.activeBorder} style={{ flexShrink: 0 }}>
        <path d={path} />
      </svg>
    );
  };

  // --- MOBILE: bottom tab bar + full-screen content ---
  if (isMobile) {
    return (
      <div data-element="app-root" style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: THEME.contentBg, overflow: 'hidden', fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>
        {/* Mobile Top Bar */}
        {auth && auth.isLoggedIn && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 16px', paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))',
            background: THEME.sidebarBg, borderBottom: `1px solid ${THEME.sidebarBorder}`,
            flexShrink: 0,
          }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: THEME.textActive }}>CoachPlaybook</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                data-element="app-theme-toggle-mobile"
                onClick={toggleTheme}
                title={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '30px', height: '30px', borderRadius: '6px',
                  border: `1px solid ${THEME.sidebarBorder}`, background: 'transparent',
                  cursor: 'pointer', color: THEME.textMuted, padding: 0,
                }}
              >
                {themeMode === 'dark' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58a.996.996 0 00-1.41 0 .996.996 0 000 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37a.996.996 0 00-1.41 0 .996.996 0 000 1.41l1.06 1.06c.39.39 1.03.39 1.41 0a.996.996 0 000-1.41l-1.06-1.06zm1.06-10.96a.996.996 0 000-1.41.996.996 0 00-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36a.996.996 0 000-1.41.996.996 0 00-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3a9 9 0 109 9c0-.46-.04-.92-.1-1.36a5.389 5.389 0 01-4.4 2.26 5.403 5.403 0 01-3.14-9.8c-.44-.06-.9-.1-1.36-.1z" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => auth.logout()}
                style={{
                  background: 'transparent', border: `1px solid ${THEME.sidebarBorder}`,
                  borderRadius: '6px', padding: '4px 10px', cursor: 'pointer',
                  color: THEME.textMuted, fontSize: '11px', fontWeight: 500,
                  display: 'flex', alignItems: 'center', gap: '4px',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        )}
        {/* Main Content */}
        <main data-element="app-main-content" style={{ flex: '1 1 0%', overflowY: 'auto', overflowX: 'hidden', minHeight: 0, paddingBottom: '72px' }}>
          {renderContent()}
        </main>

        {/* Mobile Bottom Tab Bar */}
        <nav
          data-element="app-mobile-tabbar"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            height: '64px',
            background: THEME.mobilBarBg,
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            borderTop: `1px solid ${THEME.sidebarBorder}`,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          {mobileNavItems.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                data-element={`app-nav-mobile-${item.id}`}
                onClick={() => handleNavClick(item.id)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '6px 0',
                  gap: '2px',
                  flex: 1,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill={isActive ? THEME.activeBorder : THEME.textMuted}
                  style={{ transition: 'fill 0.2s ease' }}
                >
                  <path d={NAV_ICONS[item.iconKey]} />
                </svg>
                {/* Active dot indicator */}
                {isActive && (
                  <div style={{
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    background: THEME.accentDot,
                    marginTop: '2px',
                  }} />
                )}
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  // --- DESKTOP / TABLET: sidebar + content ---
  return (
    <div data-element="app-root" style={{ display: 'flex', height: '100vh', background: THEME.contentBg, overflow: 'hidden', fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>

      {/* Sidebar */}
      <aside
        data-element="app-sidebar"
        style={{
          width: isTablet && sidebarCollapsed ? '0px' : '220px',
          minWidth: isTablet && sidebarCollapsed ? '0px' : '220px',
          background: THEME.sidebarBg,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          borderRight: `1px solid ${THEME.sidebarBorder}`,
          overflow: 'hidden',
          transition: 'width 0.3s ease, min-width 0.3s ease, opacity 0.3s ease',
          opacity: isTablet && sidebarCollapsed ? 0 : 1,
        }}
      >
        {/* Logo Area */}
        <div
          data-element="app-sidebar-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '20px 16px 16px',
            borderBottom: `1px solid ${THEME.sidebarBorder}`,
          }}
        >
          {renderSportIcon()}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{
              fontSize: '15px',
              fontWeight: 700,
              color: THEME.textActive,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}>CoachPlaybook</span>
            {teamName && (
              <span style={{
                fontSize: '11px',
                color: THEME.textMuted,
                marginTop: '2px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '140px',
              }}>{teamName}</span>
            )}
          </div>
          {isTablet && !sidebarCollapsed && (
            <button
              onClick={() => setSidebarCollapsed(true)}
              title="Hide sidebar"
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={THEME.textMuted}>
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          )}
        </div>

        {/* Navigation Items */}
        <nav data-element="app-sidebar-nav" style={{ flex: 1, overflowY: 'auto', padding: '8px 8px' }}>
          {navItems.map((item) => {
            if (item.id === 'divider') {
              return (
                <div
                  key="divider"
                  style={{
                    height: '1px',
                    background: THEME.sidebarBorder,
                    margin: '8px 8px',
                  }}
                />
              );
            }

            const isActive = activeTab === item.id;

            return (
              <button
                key={item.id}
                data-element={`app-nav-${item.id}`}
                onClick={() => handleNavClick(item.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  height: '40px',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '13px',
                  fontWeight: isActive ? 600 : 400,
                  fontFamily: 'inherit',
                  color: isActive ? THEME.textActive : THEME.textMuted,
                  background: isActive ? THEME.activeItemBg : 'transparent',
                  borderLeft: isActive ? `3px solid ${THEME.activeBorder}` : '3px solid transparent',
                  transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
                  marginBottom: '2px',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = THEME.hoverBg;
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'transparent';
                }}
              >
                {renderNavIcon(item.iconKey, isActive)}
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div
          data-element="app-sidebar-footer"
          style={{
            padding: '12px 16px',
            borderTop: `1px solid ${THEME.sidebarBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              data-element="app-version"
              style={{
                fontSize: '10px',
                fontWeight: 500,
                color: THEME.textDim,
                background: THEME.versionBg,
                padding: '3px 8px',
                borderRadius: '10px',
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >v{APP_VERSION}</span>
            <button
              data-element="app-theme-toggle"
              onClick={toggleTheme}
              title={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                borderRadius: '8px',
                border: `1px solid ${THEME.sidebarBorder}`,
                background: THEME.versionBg,
                cursor: 'pointer',
                color: THEME.textMuted,
                transition: 'color 0.15s, border-color 0.15s, background 0.15s',
                padding: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = THEME.activeBorder; e.currentTarget.style.borderColor = THEME.activeBorder; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = THEME.textMuted; e.currentTarget.style.borderColor = THEME.sidebarBorder; }}
            >
              {themeMode === 'dark' ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58a.996.996 0 00-1.41 0 .996.996 0 000 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37a.996.996 0 00-1.41 0 .996.996 0 000 1.41l1.06 1.06c.39.39 1.03.39 1.41 0a.996.996 0 000-1.41l-1.06-1.06zm1.06-10.96a.996.996 0 000-1.41.996.996 0 00-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36a.996.996 0 000-1.41.996.996 0 00-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 3a9 9 0 109 9c0-.46-.04-.92-.1-1.36a5.389 5.389 0 01-4.4 2.26 5.403 5.403 0 01-3.14-9.8c-.44-.06-.9-.1-1.36-.1z" />
                </svg>
              )}
            </button>
          </div>
          {auth && auth.isLoggedIn && (
            <button
              onClick={() => auth.logout()}
              title="Sign out"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'transparent',
                border: `1px solid ${THEME.sidebarBorder}`,
                borderRadius: '6px',
                padding: '5px 10px',
                cursor: 'pointer',
                color: THEME.textMuted,
                fontSize: '11px',
                fontWeight: 500,
                transition: 'color 0.15s, border-color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = '#f87171'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = THEME.textMuted; e.currentTarget.style.borderColor = THEME.sidebarBorder; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
              </svg>
              Logout
            </button>
          )}
        </div>
      </aside>

      {/* Tablet Sidebar Toggle */}
      {isTablet && sidebarCollapsed && (
        <button
          data-element="app-tablet-sidebar-toggle"
          onClick={() => setSidebarCollapsed(false)}
          style={{
            position: 'fixed',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 50,
            background: THEME.activeBorder,
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            padding: '12px 6px',
            borderRadius: '0 8px 8px 0',
            boxShadow: '2px 0 12px rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#2563eb'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = THEME.activeBorder; }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </button>
      )}

      {/* Main Content Area */}
      <main data-element="app-main-content" style={{ flex: '1 1 0%', overflowY: 'auto', overflowX: 'hidden', minWidth: 0, height: '100%' }}>
        {renderContent()}
      </main>
    </div>
  );
};

export default App;
